/**
 * The signed-in person's own photograph beside "My Profile".
 *
 * Identity puts the standard OIDC `picture` claim in the ID token: an absolute link to the image on
 * Identity's own host, present only when the person has set a picture. Check-ins never stores the
 * image. The browser loads it straight from Identity — sending Identity's session cookie, since both
 * apps are on rockchurch.com — and caches it.
 */

/** The `picture` claim as a usable absolute http(s) link, or null when it is absent or unusable. */
export function pictureClaim(payload: Record<string, unknown> | null): string | null {
  const value = payload?.['picture'];
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  // Absolute http(s) only. A relative path would resolve against Check-ins' own host, where no such
  // image exists, and no other scheme has any business in an <img>.
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Which picture the toolbar should show, or null for the generic icon.
 *
 * A picture that failed to load is suppressed so the icon shows instead of a broken image — which is
 * what happens if Identity's session cookie has expired while Check-ins is still signed in, and the
 * image request is sent to Identity's sign-in page. A different, newer picture is still tried.
 */
export function profilePictureToShow(picture: string | null, failed: string | null): string | null {
  return picture !== null && picture !== failed ? picture : null;
}
